// pages/Categories.jsx
import React, { useState, useEffect } from "react";
import {
  Search,
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  FolderTree,
  X,
  LayoutGrid,
  CheckCircle,
  XCircle,
  Radio,
  Power,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import ActionModal from "../components/Modals/ActionModal";
import { useNavigate } from "react-router-dom";
import api from "../../../services/api";
import { Pagination } from "../../../services/helper";

export default function Categories() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [activeMenu, setActiveMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, position: "bottom" });

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, category: null });

  // React Hook Form — is_active stored as string "1"/"0" to match radio value attributes
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { name: "", description: "", is_active: "1" },
  });

  // Fetch Data
  const { data, isLoading } = useQuery({
    queryKey: ["stream-categories", page, search],
    queryFn: () => api.getStreamCategories(page,10, search),
    keepPreviousData: true,
  });

  const categories = data?.data || [];
  const pagination = data?.pagination || { page: 1, totalPages: 1, hasNext: false, hasPrev: false };

  const statsData = data?.stats || {};
  useEffect(() => {
    setPage(1);
  }, [search]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (payload) => api.createStreamCategory(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stream-categories"] });
      setShowAddModal(false);
      reset();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api.updateStreamCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stream-categories"] });
      setShowEditModal(false);
      setSelectedCategory(null);
      reset();
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (id) => api.toggleStreamCategory(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["stream-categories"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.deleteStreamCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stream-categories"] });
      setDeleteModal({ isOpen: false, category: null });
    },
  });

  // Stats
  const stats = [
    { label: "Total Categories", value: statsData.totalCategories?.toLocaleString() || "0", icon: LayoutGrid, highlight: false },
    { label: "Active Categories", value: statsData.activeCategories?.toLocaleString() || "0", icon: CheckCircle, highlight: false },
    { label: "Inactive Categories", value: statsData.inactiveCategories?.toLocaleString() || "0", icon: XCircle, highlight: false },
    { label: "Faith Streams", value: statsData.totalMediaItems?.toLocaleString() || "0", icon: Radio, highlight: true },
  ];

  const toggleMenu = (id, event) => {
    if (activeMenu === id) {
      setActiveMenu(null);
      return;
    }

    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    const dropdownHeight = 140;
    const spaceBelow = window.innerHeight - rect.bottom;

    let top = rect.bottom + window.scrollY + 5;
    let position = "bottom";

    if (spaceBelow < dropdownHeight) {
      top = rect.top + window.scrollY - dropdownHeight - 5;
      position = "top";
    }

    setMenuPosition({ top, left: rect.right - 160, position });
    setActiveMenu(id);
  };

  const openEdit = (category) => {
    setSelectedCategory(category);

    // Convert is_active to string so radio value matching works correctly
    reset({
      name: category.name,
      description: category.description || "",
      is_active: String(category.is_active),
    });

    setShowEditModal(true);
    setActiveMenu(null);
  };

  const onSubmit = (formData) => {
    const payload = {
      name: formData.name,
      description: formData.description || "",
      is_active: Number(formData.is_active), // convert "1"/"0" back to number for API
    };

    if (showEditModal && selectedCategory?.id) {
      updateMutation.mutate({ id: selectedCategory.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.category) {
      deleteMutation.mutate(deleteModal.category.id);
    }
  };

  const toggleStatus = (id) => {
    toggleMutation.mutate(id);
    setActiveMenu(null);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setSelectedCategory(null);
    reset({ name: "", description: "", is_active: "1" });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeMenu && !e.target.closest(".dropdown-menu") && !e.target.closest(".dropdown-trigger")) {
        setActiveMenu(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [activeMenu]);

  return (
    <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }} className="bg-gray-50">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 mb-8">
        <div>
          <h1 className="text-[22px] sm:text-[28px] md:text-[33px] lg:text-[30px] 2xl:text-[30px] font-bold text-gray-900 mb-1 leading-tight">
            Faith Stream Categories
          </h1>
          <p className="text-gray-500 text-[14px] sm:text-[15px] md:text-[16px] lg:text-[17px]">
            Manage categories for Faith Stream content
          </p>
        </div>

        <button
          onClick={() => {
            reset({ name: "", description: "", is_active: "1" });
            setShowAddModal(true);
          }}
          className="flex items-center justify-center gap-2 w-full sm:w-auto h-[48px] px-5 rounded-xl bg-[#1F3A60] text-white font-medium hover:bg-[#172e4e]"
        >
          <Plus size={18} />
          Add Category
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-4 gap-4 mb-8">
        {stats.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div
              key={i}
              className={`min-h-[174px] rounded-3xl px-5 sm:px-6 py-6 sm:py-7 transition-all ${
                stat.highlight ? "bg-gradient-to-r from-[#1F3A60] to-[#4078C6] text-white" : "bg-white"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <p className={`text-sm sm:text-[16px] font-medium ${stat.highlight ? "text-blue-100" : "text-gray-500"}`}>
                  {stat.label}
                </p>
                <div className={`w-12 h-12 sm:w-[51px] sm:h-[51px] rounded-xl flex items-center justify-center ${stat.highlight ? "bg-white/10" : "bg-gray-100"}`}>
                  <Icon size={28} className={stat.highlight ? "text-white" : "text-gray-400"} />
                </div>
              </div>
              <p className={`text-3xl sm:text-4xl lg:text-[45px] font-bold tracking-[-1px] ${stat.highlight ? "text-white" : "text-gray-900"}`}>
                {stat.value}
              </p>
            </div>
          );
        })}
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-3xl overflow-hidden">
        <div className="px-4 md:px-6 py-4 border-b border-gray-100 flex justify-between items-center">
          <span className="font-semibold text-gray-700">All Categories</span>
          <div className="relative w-80">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search categories..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 py-2.5 border border-gray-200 rounded-xl focus:border-[#1F3A60] bg-gray-50"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-400 uppercase">Category</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-400 uppercase">Description</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-400 uppercase">Status</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-400 uppercase">Created</th>
                <th className="text-center px-6 py-4 text-xs font-semibold text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={5} className="text-center py-12">Loading...</td></tr>
              ) : categories.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-12 text-gray-400">No categories found</td></tr>
              ) : (
                categories.map((cat) => (
                  <tr key={cat.id} className="border-b hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#1F3A60] to-[#2D4A7C] rounded-xl flex items-center justify-center">
                          <FolderTree size={18} className="text-white" />
                        </div>
                        <span className="font-semibold">{cat.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">{cat.description || "—"}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium border ${
                        cat.is_active === 1
                          ? "text-teal-600 bg-teal-50 border-teal-200"
                          : "text-red-500 bg-red-50 border-red-200"
                      }`}>
                        {cat.is_active === 1 ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(cat.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button onClick={(e) => toggleMenu(cat.id, e)} className="dropdown-trigger">
                        <MoreHorizontal size={24} strokeWidth={2.5} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination pagination={pagination} onPageChange={setPage} />

      {/* Dropdown Menu */}
      {activeMenu && (
        <div className="fixed z-50 dropdown-menu" style={{ top: menuPosition.top, left: menuPosition.left }}>
          <div className="w-44 bg-white rounded-xl shadow-xl border py-1">
            {(() => {
              const cat = categories.find(c => c.id === activeMenu);
              if (!cat) return null;
              return (
                <>
                  <button onClick={() => openEdit(cat)} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50">
                    <Edit size={14} /> Edit
                  </button>
                  <button onClick={() => toggleStatus(cat.id)} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50">
                    <Power size={14} /> {cat.is_active === 1 ? "Deactivate" : "Activate"}
                  </button>
                  <button
                    onClick={() => {
                      setDeleteModal({ isOpen: true, category: cat });
                      setActiveMenu(null);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Add / Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/60 z-[999] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="px-6 pt-6 pb-4 border-b flex items-center justify-between">
                <h2 className="text-2xl font-bold">
                  {showEditModal ? "Edit Category" : "New Category"}
                </h2>
                <button type="button" onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                  <X size={24} />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Category Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("name", { required: "Name is required" })}
                    className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:border-[#1F3A60]"
                    placeholder="e.g., Morning Worship"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Description</label>
                  <textarea
                    {...register("description")}
                    rows={4}
                    className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:border-[#1F3A60] resize-none"
                    placeholder="Describe this category..."
                  />
                </div>

                {/* Status — values are strings "1"/"0" to match HTML radio behavior */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Status</label>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="1"
                        {...register("is_active")}
                        className="w-4 h-4 accent-[#1F3A60]"
                      />
                      <span className="text-gray-700">Active</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="0"
                        {...register("is_active")}
                        className="w-4 h-4 accent-[#1F3A60]"
                      />
                      <span className="text-gray-700">Inactive</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="px-6 py-5 border-t flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-2.5 border rounded-xl hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-6 py-2.5 bg-[#1F3A60] text-white rounded-xl hover:bg-[#172e4e] disabled:opacity-70 flex items-center gap-2"
                >
                  {(createMutation.isPending || updateMutation.isPending) && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  {createMutation.isPending || updateMutation.isPending
                    ? "Saving..."
                    : showEditModal
                    ? "Update Category"
                    : "Add Category"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ActionModal
        isOpen={deleteModal.isOpen}
        onRequestClose={() => setDeleteModal({ isOpen: false, category: null })}
        onConfirm={handleDeleteConfirm}
        actionType="delete"
        title={deleteModal.category?.name}
        itemName="Category"
        isLoading={deleteMutation.isPending}
        dangerColor="#DC2626"
        customDescription={`This will permanently delete "${deleteModal.category?.name}" and all associated content.`}
      />
    </div>
  );
}